import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { getPageOfQuestion } from '../utils/assessmentsPageUtils';
import {
  selectCurrentPage,
  setActiveQuestionId,
  selectSkippedQuestionNumbers,
  setSkippedQuestions,
  selectSkipLogicMap,
  selectQuestionIdToNumberMap,
} from '../assessmentsSlice';
import { useAssessmentsQuestions } from './useAssessmentsQuestions';
import { HEADER_OFFSET_DESKTOP_TABLET, HEADER_OFFSET_MOBILE } from '../constants';
import {
  getDisabledQuestionNumbers,
  getSkippedQuestionsFromAnswers,
  getTargetIdForQuestionWithAnswer,
} from '../utils/assessmentsSkiplogic.utils';
import { TriggerSkipLogic } from '../types';
import { useAssessmentsPages } from './useAssessmentsPages';
import { useAssessmentsContext } from './useAssessmentsContext';
/**
 * Hook to manage skip logic for assessment questions.
 * Consolidates ref management, scroll behavior, and page navigation.
 */
export const useAssessmentsSkipLogic = () => {
  const dispatch = useDispatch();
  const { isMobile } = useDeviceType();
  const currentPage = useSelector(selectCurrentPage);
  const skippedQuestionNumbers = useSelector(selectSkippedQuestionNumbers);
  const questionIdToNumberMap = useSelector(selectQuestionIdToNumberMap);
  const skipMap = useSelector(selectSkipLogicMap);

  const { questions } = useAssessmentsQuestions();
  const { setPage } = useAssessmentsPages();

  // Get refs from context (singleton across all hook usages)
  const { questionRefs, refCallbacks, pendingScrollTarget, assessmentsFormRef } =
    useAssessmentsContext();

  const headerOffset = useMemo(
    () => (isMobile ? HEADER_OFFSET_MOBILE : HEADER_OFFSET_DESKTOP_TABLET),
    [isMobile],
  );

  /**
   * Scrolls to an element with offset for the sticky header and focuses it.
   * Scrolls the assessments form itself, since that's the actual scrolling
   * container (not the window).
   */
  const scrollToElement = useCallback(
    (element: HTMLElement) => {
      // Defer measurement to the next frame so getBoundingClientRect() reads the
      // layout *after* React has committed any pending re-render (e.g. a collapsing
      // sub-question card). Measuring synchronously would use the stale, still-expanded
      // layout and scroll past the top of the target question.
      requestAnimationFrame(() => {
        const scrollContainer = assessmentsFormRef.current;
        if (!scrollContainer) return;

        const elementPosition = element.getBoundingClientRect().top;
        const containerPosition = scrollContainer.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition - containerPosition + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        element.focus({ preventScroll: true });
      });
    },
    [headerOffset, assessmentsFormRef],
  );

  /**
   * Resets the assessments form's scroll position to the very top.
   */
  const scrollToTop = useCallback(() => {
    requestAnimationFrame(() => {
      assessmentsFormRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [assessmentsFormRef]);

  /**
   * Clears all refs. Call this on page change events.
   */
  const clearRefs = useCallback(() => {
    questionRefs.current.clear();
    refCallbacks.current.clear();
  }, []);

  /**
   * Returns a STABLE ref callback for a question ID.
   */
  const registerRef = useCallback(
    (questionId: number) => {
      if (!refCallbacks.current.has(questionId)) {
        refCallbacks.current.set(questionId, (element: HTMLElement | null) => {
          if (element) {
            questionRefs.current.set(questionId, element);
            // If this was the pending scroll target, scroll now
            if (pendingScrollTarget.current === questionId) {
              scrollToElement(element);
              pendingScrollTarget.current = null;
            }
          } else {
            questionRefs.current.delete(questionId);
          }
        });
      }
      return refCallbacks.current.get(questionId)!;
    },
    [scrollToElement],
  );

  /**
   * Scrolls to a question by its ID.
   */
  const scrollToQuestion = useCallback(
    (questionId: number): boolean => {
      const element = questionRefs.current.get(questionId);
      if (element) {
        scrollToElement(element);
        return true;
      }
      return false;
    },
    [scrollToElement],
  );

  /**
   * Main skip logic handler.
   * Determines actual target (considering required questions), changes page if needed, and scrolls.
   *
   * @param targetQuestionId - The intended skip target (from questionNext)
   */
  const skipToQuestion = useCallback(
    (targetQuestionId: number) => {
      if (targetQuestionId === null) return;

      const targetQuestionNumber =
        questionIdToNumberMap[targetQuestionId] ??
        questions?.find((question) => question.id === targetQuestionId)?.questionNumber;
      if (targetQuestionNumber === undefined) return;

      // Check if target is on a different page
      const targetPage = getPageOfQuestion(targetQuestionNumber);
      if (targetPage === null) return;

      if (targetPage !== currentPage) {
        // Target is on different page - set pending scroll and navigate
        pendingScrollTarget.current = targetQuestionId;
        clearRefs();
        setPage(targetPage);
      } else {
        // Target is on current page - scroll immediately
        scrollToQuestion(targetQuestionId);
        pendingScrollTarget.current = null;
      }
    },
    [currentPage, clearRefs, scrollToQuestion, questionIdToNumberMap, questions, setPage],
  );

  /**
   * Marks all questions between the current and target as disabled (skipped)
   * and dispatches the updated list to the store.
   */
  const disableSkippedQuestions = useCallback(
    (questionNumber: number, targetQuestionNumber: number) => {
      const disabledQuestionNumbers = getDisabledQuestionNumbers(
        skippedQuestionNumbers,
        questionNumber,
        targetQuestionNumber,
      );
      dispatch(setSkippedQuestions(disabledQuestionNumbers));
    },
    [skippedQuestionNumbers, dispatch],
  );

  /**
   * Handles the "Next" button action for a given question and question type that auto-scrolls on answer.
   * Resolves the skip target from the skip map, navigates/scrolls to it,
   * disables intermediate questions, and sets the target as the active question.
   */
  const triggerSkipLogic: TriggerSkipLogic = useCallback(
    ({ currentQuestionId, value, allFormValues, performScroll = false }) => {
      const question = questions?.find((entry) => entry.id === currentQuestionId);

      if (!question) return;

      const targetQuestionId = getTargetIdForQuestionWithAnswer({
        currentQuestionId,
        questionType: question.type,
        value,
        skipMap,
      });

      if (targetQuestionId === null) return;

      const currentQuestionNumber =
        questionIdToNumberMap[currentQuestionId] ?? question.questionNumber;
      const targetQuestionNumber =
        questionIdToNumberMap[targetQuestionId] ??
        questions?.find((entry) => entry.id === targetQuestionId)?.questionNumber;

      if (targetQuestionNumber === undefined) return;

      if (allFormValues) {
        const derivedSkippedQuestionNumbers = getSkippedQuestionsFromAnswers({
          questions: questions ?? [],
          formValues: allFormValues,
          skipMap,
          questionIdToNumberMap,
        });
        dispatch(setSkippedQuestions(derivedSkippedQuestionNumbers));
      } else {
        disableSkippedQuestions(currentQuestionNumber, targetQuestionNumber);
      }

      if (performScroll) {
        dispatch(setActiveQuestionId(targetQuestionId));
        skipToQuestion(targetQuestionId);
      }
    },
    [skipToQuestion, disableSkippedQuestions, questions, dispatch, questionIdToNumberMap, skipMap],
  );

  /**
   * Handles the "Jump to" action for a given question.
   * No skip logic is applied; it simply sets the active question and scrolls to it.
   * Used primarily for bringing the focus to a question that was skipped.
   */
  const jumpToQuestion = useCallback(
    (questionId: number) => {
      dispatch(setActiveQuestionId(questionId));
      skipToQuestion(questionId);
    },
    [dispatch, skipToQuestion],
  );

  return {
    registerRef,
    clearRefs,
    scrollToQuestion,
    triggerSkipLogic,
    jumpToQuestion,
    scrollToElement,
    scrollToTop,
  };
};
